import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useState } from 'react';
import classes from './DetailContent.module.scss';

// notes: the team's write-up (the inspection's `notes`), shown above the photos.
const InspectionGallery = forwardRef(({ images, notes, onClose }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const galleryRef = useRef(null);

    // Expose scrollToGallery method to parent component
    useImperativeHandle(ref, () => ({
        scrollToGallery: () => {
            galleryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }));

    // Helper function to check if the URL is a video
    const isVideo = (url) => {
        if (!url) return false;
        const videoExtensions = ['.mp4', '.MP4', '.mov', '.MOV', '.avi', '.AVI', '.webm', '.WEBM'];
        return videoExtensions.some(ext => url.endsWith(ext));
    };

    const openModal = (index) => {
        setCurrentIndex(index);
        setIsOpen(true);
    }

    const closeModal = () => {
        setIsOpen(false);
    }

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        )
    }

    return (
        <>
            <div className={classes.inspectionGalleryWrap}>
                <div className={classes.inspectionGallery} ref={galleryRef}>
                    <div className={classes.headerContainer}>
                        <h2>Inspection Gallery</h2>
                        <button 
                            className={classes.closeButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Use a simpler scroll approach to avoid jittering
                                window.scrollTo(0, 500);
                                
                                // Wait for scroll to complete before hiding content
                                setTimeout(() => {
                                    onClose();
                                }, 300);
                            }}
                            aria-label="Close inspection gallery"
                        >
                            ×
                        </button>
                    </div>
                    <div className={classes.gallery_container}>
                        {notes && (
                            <section className={classes.inspectionNotes}>
                                <h3>
                                    <i className="fas fa-clipboard-check" aria-hidden="true"></i>
                                    Inspection report
                                </h3>
                                <p>{notes}</p>
                            </section>
                        )}

                        {/* Image/Video Grid */}
                        <div className={classes.image_grid}>
                            {images.map((item, index) => (
                                isVideo(item) ? (
                                    <div 
                                        key={index} 
                                        className={classes.thumbnailWrapper}
                                        onClick={() => openModal(index)}
                                    >
                                        <video 
                                            src={item}
                                            className={classes.thumbnail}
                                            preload="metadata"
                                            aria-label={`Video ${index + 1}`}
                                        />
                                        <div className={classes.videoOverlay}>
                                            <i className="fas fa-play-circle"></i>
                                        </div>
                                    </div>
                                ) : (
                                    <img
                                        key={index}
                                        src={item}
                                        alt={`Thumbnail ${index + 1}`}
                                        className={classes.thumbnail}
                                        onClick={() => openModal(index)}
                                    />
                                )
                            ))}
                        </div>

                        {/* Modal */}
                        {isOpen && (
                            <div className={classes.modal_overlay}>
                                <div className={classes.modal_content}>
                                    <button className={classes.close_button} onClick={closeModal}>
                                        <b>✕</b>
                                    </button>
                                    
                                    {isVideo(images[currentIndex]) ? (
                                        <video
                                            src={images[currentIndex]}
                                            className={classes.modal_image}
                                            controls
                                            autoPlay
                                            aria-label={`Video ${currentIndex + 1}`}
                                        />
                                    ) : (
                                        <img
                                            src={images[currentIndex]}
                                            alt={`Large ${currentIndex + 1}`}
                                            className={classes.modal_image}
                                        />
                                    )}
                                    
                                    <div className={classes.modal_controls}>
                                        <button className={classes.nav_button} onClick={goToPrevious}>
                                            ◀ Previous
                                        </button>
                                        <button className={classes.nav_button} onClick={goToNext}>
                                            Next ▶
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default InspectionGallery;